import { decodeSaveArtifact } from "./match-persistence.js";
import type { CertifiedCheckpoint } from "./recovery-types.js";
import { validateCertified } from "./recovery-validation.js";
import { decodeBoundedRoomPacket, encodeRoomPacket } from "./room-packet.js";
import type { ConflictConfig } from "./types.js";

export interface OnlineRoomSave {
  readonly format: "kilnfront-room-save";
  readonly formatVersion: 1;
  readonly config: ConflictConfig;
  readonly certified: CertifiedCheckpoint;
}

export function encodeOnlineRoomSave(
  config: ConflictConfig,
  certified: CertifiedCheckpoint,
): string {
  validateCertified(certified, certified.checkpoint.voters);
  return encodeRoomPacket({
    format: "kilnfront-room-save",
    formatVersion: 1,
    config: structuredClone(config),
    certified: structuredClone(certified),
  } satisfies OnlineRoomSave);
}

export function decodeOnlineRoomSave(raw: string): OnlineRoomSave {
  const value = decodeBoundedRoomPacket<OnlineRoomSave>(raw, 4_194_304);
  if (
    !value ||
    value.format !== "kilnfront-room-save" ||
    value.formatVersion !== 1 ||
    !value.config ||
    !value.certified
  )
    throw new TypeError("Invalid online room save");
  validateCertified(value.certified, value.certified.checkpoint.voters);
  validateOnlineRoomCheckpoint(value.config, value.certified.checkpoint);
  return Object.freeze(structuredClone(value));
}

export function validateOnlineRoomCheckpoint(
  config: ConflictConfig,
  checkpoint: CertifiedCheckpoint["checkpoint"],
): void {
  const saved = decodeSaveArtifact(checkpoint.save);
  if (configIdentity(config) !== configIdentity(saved.config))
    throw new TypeError("Online room save configuration does not match its checkpoint");
  if (
    saved.snapshot.gameTimeMs !== checkpoint.gameTimeMs ||
    saved.snapshot.revision.toString() !== checkpoint.revision
  )
    throw new TypeError("Online room save metadata does not match its checkpoint");
}

function configIdentity(config: ConflictConfig): string {
  return JSON.stringify({
    rootSeed: config.rootSeed,
    stepMs: config.stepMs,
    mapId: config.mapId,
    mode: config.mode,
    durationPreset: config.durationPreset,
    eventSetting: config.eventSetting,
    seats: config.seats.map((seat) => ({
      id: seat.id,
      name: seat.name,
      adjacentTo: seat.adjacentTo,
      teamId: seat.teamId,
      bot: seat.bot,
    })),
  });
}
